import {
  authorizeUrl,
  challengeFor,
  clearAutoAttempt,
  exchangeCode,
  randomUrlSafe,
  readCallback,
  recentAutoAttempt,
  recordAutoAttempt,
  refreshTokens,
  rememberPending,
  safeReturnPath,
  takePending,
} from './pkce.js'
import type { AuthConfig, TokenSet } from './pkce.js'

/**
 * Holds the tokens in memory only — id, access and refresh alike.
 *
 * Not localStorage or sessionStorage: a token in storage survives the tab and is
 * readable by any injected script. In memory the blast radius of an XSS is
 * bounded by the page's lifetime. sessionStorage carries only the PKCE
 * verifier, the state, the path to come back to and a timestamp marking an
 * automatic attempt — nothing that authenticates anyone. A test pins that.
 *
 * What that costs, and how it is paid for (IM-170):
 *
 * - **A reload has no token**, so the console redirects to the authorize
 *   endpoint on load, carrying the path being viewed. While the Identity
 *   Center session is valid Cognito answers straight back with a code and no
 *   prompt. Cognito does not support `prompt=none`, so there is no way to ask
 *   "only if it needs nothing from the user" — which is why a loop guard
 *   exists: an automatic attempt that comes back without a token inside
 *   `AUTO_ATTEMPT_WINDOW_MS` shows the Sign in screen instead of redirecting
 *   again. Until IM-170 this comment said a reload "re-runs the silent
 *   redirect" and nothing did; the Sign in button was the only thing that
 *   ever redirected.
 * - **The id token lasts 15 minutes**, so a session in use renews itself
 *   `RENEW_AHEAD_MS` before expiry with the refresh token, up to that token's
 *   own lifetime (an hour, `infra/src/constructs/identity.ts`). After that the
 *   session ends and says so; it does not redirect by itself, because a timer
 *   that navigated away would throw out whatever someone was typing.
 */
let current: TokenSet | undefined
/** Kept so a renewal can reach the same token endpoint the sign-in used. */
let authConfig: AuthConfig | undefined
let renewTimer: ReturnType<typeof setTimeout> | undefined
/** One renewal in flight at a time, however many requests found no token. */
let renewing: Promise<boolean> | undefined
const endedListeners = new Set<(reason: string) => void>()

/** How far ahead of the id token's expiry a renewal is attempted. */
export const RENEW_AHEAD_MS = 60_000

export const SESSION_ENDED = 'Your session has ended. Sign in again to continue.'

export interface BeginSignInOptions {
  /** Where to land afterwards: a same-origin path, anything else is dropped. */
  readonly returnTo?: string
  /**
   * Started by the console rather than by a click. Recorded, so that the
   * attempt which fails to come back with a token is the last one.
   */
  readonly automatic?: boolean
  readonly now?: () => number
}

export const beginSignIn = async (
  config: AuthConfig,
  storage: Storage,
  options: BeginSignInOptions = {},
): Promise<string> => {
  const verifier = randomUrlSafe()
  const state = randomUrlSafe(16)
  const returnTo = safeReturnPath(options.returnTo)
  rememberPending(
    storage,
    returnTo === undefined ? { verifier, state } : { verifier, state, returnTo },
  )
  if (options.automatic === true) recordAutoAttempt(storage, (options.now ?? Date.now)())
  return authorizeUrl(config, await challengeFor(verifier), state)
}

/**
 * Whether the console should redirect on its own rather than show a button.
 *
 * False once an automatic attempt in this tab has come back without a token,
 * which is the whole of the loop guard.
 */
export const mayAttemptAutomatically = (storage: Storage, now: number = Date.now()): boolean =>
  !recentAutoAttempt(storage, now)

/**
 * A sign-in that did not complete, carrying where the person was going so
 * the screen that explains it can be the screen they asked for.
 */
export class SignInFailed extends Error {
  readonly returnTo: string | undefined

  constructor(message: string, returnTo: string | undefined) {
    super(message)
    this.returnTo = returnTo
  }
}

export interface SignedIn {
  /** Validated again here; `undefined` means "stay where the callback is". */
  readonly returnTo: string | undefined
}

export const completeSignIn = async (
  config: AuthConfig,
  storage: Storage,
  search: string,
  now: () => number = Date.now,
  fetchImpl: typeof fetch = fetch,
): Promise<SignedIn> => {
  const pending = takePending(storage)
  const returnTo = pending?.returnTo
  const result = readCallback(search, pending?.state)

  if (result.kind === 'error') throw new SignInFailed(result.message, returnTo)
  if (result.kind === 'none')
    throw new SignInFailed('No authorization code in the callback', returnTo)
  if (pending === undefined)
    throw new SignInFailed('No sign-in was in progress in this tab', returnTo)

  let tokens: TokenSet
  try {
    tokens = await exchangeCode(config, result.code, pending.verifier, now, fetchImpl)
  } catch (error: unknown) {
    throw new SignInFailed(error instanceof Error ? error.message : 'Sign-in failed', returnTo)
  }
  // Cleared only on success: the marker is what says "the last automatic try
  // did not work", and after a failure that is exactly true.
  clearAutoAttempt(storage)
  authConfig = config
  current = tokens
  scheduleRenewal(now)
  return { returnTo }
}

export const currentToken = (now: () => number = Date.now): string | undefined =>
  current !== undefined && current.expiresAt > now() ? current.idToken : undefined

const stopTimer = (): void => {
  if (renewTimer !== undefined) clearTimeout(renewTimer)
  renewTimer = undefined
}

const scheduleRenewal = (now: () => number): void => {
  stopTimer()
  if (current?.refreshToken === undefined) return
  const delay = Math.max(0, current.expiresAt - RENEW_AHEAD_MS - now())
  renewTimer = setTimeout(() => {
    renewTimer = undefined
    void renewSession(now)
  }, delay)
}

const endSession = (): void => {
  const hadSession = current !== undefined
  current = undefined
  stopTimer()
  if (!hadSession) return
  for (const listener of endedListeners) listener(SESSION_ENDED)
}

/**
 * Renews the tokens, once, however many callers ask at the same moment.
 *
 * Resolves `false` when there is nothing to renew with or the refresh token
 * has been refused — expired after its hour, or revoked — and in that case the
 * session is over and the listeners are told, so the header can say so rather
 * than the next click failing without explanation.
 */
export const renewSession = (
  now: () => number = Date.now,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> => {
  if (renewing !== undefined) return renewing
  const config = authConfig
  const refreshToken = current?.refreshToken
  if (config === undefined || refreshToken === undefined) {
    endSession()
    return Promise.resolve(false)
  }
  renewing = refreshTokens(config, refreshToken, now, fetchImpl)
    .then((tokens) => {
      current = tokens
      scheduleRenewal(now)
      return true
    })
    .catch(() => {
      endSession()
      return false
    })
    .finally(() => {
      renewing = undefined
    })
  return renewing
}

/**
 * The token for a request that found none: one renewal, then give up.
 *
 * Separate from `currentToken` rather than folded into it, so the common case
 * stays synchronous and a test can stub the token without a fetch.
 */
export const renewedToken = async (
  now: () => number = Date.now,
  fetchImpl: typeof fetch = fetch,
): Promise<string | undefined> =>
  (await renewSession(now, fetchImpl)) ? currentToken(now) : undefined

/** Called with the reason when a session that existed can no longer be renewed. */
export const onSessionEnded = (listener: (reason: string) => void): (() => void) => {
  endedListeners.add(listener)
  return () => {
    endedListeners.delete(listener)
  }
}

export const signOut = (): void => {
  current = undefined
  authConfig = undefined
  renewing = undefined
  stopTimer()
}
